import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Mail, ArrowLeft, Key, Check, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const tokenFromUrl = searchParams.get("token");

  // Two modes: request reset (no token) or set new password (has token)
  const [mode] = useState(tokenFromUrl ? "reset" : "request");
  const [email, setEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [done, setDone] = useState(false);
  const [mockToken, setMockToken] = useState(null);

  const handleRequestReset = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/forgot-password`, { email: email.trim() });
      setSent(true);
      if (data._mock_reset_token) {
        setMockToken(data._mock_reset_token);
      }
      toast.success("Check your email for the reset link");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (newPassword !== confirmPassword) { toast.error("Passwords don't match"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token: tokenFromUrl, new_password: newPassword });
      setDone(true);
      toast.success("Password reset successfully!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid or expired reset link");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        {/* Back to login */}
        <button
          onClick={() => navigate("/auth")}
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back to login
        </button>

        <div className="bg-card border border-border/50 rounded-3xl p-7 shadow-xl">
          {mode === "request" && !sent ? (
            <>
              <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center mb-5">
                <Mail className="w-6 h-6 text-primary" />
              </div>
              <h1 className="text-xl font-heading font-semibold mb-1">Forgot your password?</h1>
              <p className="text-sm text-muted-foreground mb-6">Enter your email and we'll send you a reset link.</p>
              <form onSubmit={handleRequestReset} className="space-y-4">
                <input
                  data-testid="reset-email-input"
                  type="email"
                  placeholder="Your email address"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  required
                />
                <Button
                  data-testid="reset-send-btn"
                  type="submit"
                  disabled={loading}
                  className="w-full btn-omni bg-primary text-primary-foreground h-11 gap-2"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                  {loading ? "Sending..." : "Send Reset Link"}
                </Button>
              </form>
            </>
          ) : mode === "request" && sent ? (
            <>
              <div className="w-12 h-12 rounded-2xl bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-5">
                <Check className="w-6 h-6 text-green-600" />
              </div>
              <h1 className="text-xl font-heading font-semibold mb-1">Check your email</h1>
              <p className="text-sm text-muted-foreground mb-4">
                If an account with that email exists, we've sent a password reset link.
              </p>
              {/* MOCKED: Show reset link for testing */}
              {mockToken && (
                <div data-testid="mock-reset-link" className="p-3 rounded-xl bg-amber-50 dark:bg-amber-950/30 border border-amber-200/50 dark:border-amber-800/30 mb-4">
                  <p className="text-[10px] font-semibold text-amber-700 dark:text-amber-400 uppercase mb-1">Test Mode — Reset Link</p>
                  <button
                    onClick={() => navigate(`/reset-password?token=${mockToken}`)}
                    className="text-xs text-primary underline"
                  >
                    Click here to reset password
                  </button>
                </div>
              )}
              <Button
                onClick={() => navigate("/auth")}
                variant="outline"
                className="w-full rounded-full"
              >
                Back to login
              </Button>
            </>
          ) : mode === "reset" && !done ? (
            <>
              <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center mb-5">
                <Key className="w-6 h-6 text-primary" />
              </div>
              <h1 className="text-xl font-heading font-semibold mb-1">Set new password</h1>
              <p className="text-sm text-muted-foreground mb-6">Choose a strong password for your account.</p>
              <form onSubmit={handleResetPassword} className="space-y-4">
                <input
                  data-testid="new-password-input"
                  type="password"
                  placeholder="New password (min 6 characters)"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  required
                  minLength={6}
                />
                <input
                  data-testid="confirm-password-input"
                  type="password"
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  required
                />
                <Button
                  data-testid="reset-password-btn"
                  type="submit"
                  disabled={loading}
                  className="w-full btn-omni bg-primary text-primary-foreground h-11 gap-2"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Key className="w-4 h-4" />}
                  {loading ? "Resetting..." : "Reset Password"}
                </Button>
              </form>
            </>
          ) : (
            <>
              <div className="w-12 h-12 rounded-2xl bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-5">
                <Check className="w-6 h-6 text-green-600" />
              </div>
              <h1 className="text-xl font-heading font-semibold mb-1">Password reset!</h1>
              <p className="text-sm text-muted-foreground mb-6">Your password has been updated. You can now log in.</p>
              <Button
                data-testid="goto-login-btn"
                onClick={() => navigate("/auth")}
                className="w-full btn-omni bg-primary text-primary-foreground h-11"
              >
                Go to Login
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
